import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import AccessRequestSchema, {
  type AccessRequestJSON,
  AccessRequestStatus,
} from "permissions/access-requests/_schemas/access-request-schema";
import {
  isSameAccess,
  readRequestableAccess,
  type RequestableAccess,
} from "permissions/access-requests/requestable-access";

/**
 * A member asking for a permission they lack, from the people in their organization who may grant
 * it (#6596). `access` is what an approval grants, no more; it is undefined when the API named a
 * resource this bundle cannot read.
 */
export default class AccessRequest {
  public static fromJSON(json: unknown): AccessRequest | undefined {
    if (!AccessRequestSchema.isValid(json)) return undefined;
    return new AccessRequest(AccessRequestSchema.validate(json));
  }

  /** Every request in the list that this bundle can read, in the order the API sent them. */
  public static fromJSONArray(json: readonly unknown[] | undefined): AccessRequest[] {
    return (json ?? []).map((entry) => AccessRequest.fromJSON(entry)).filter((entry) => entry !== undefined);
  }

  public readonly id: Guid;
  public readonly requesterUserId: Guid;
  public readonly requesterName: string;
  public readonly access: RequestableAccess | undefined;
  /** What the access lets somebody do, such as "upload documents". */
  public readonly accessDescription: string;
  /** What the requester was doing when they asked, such as "Upload files to Patent filing". */
  public readonly context: string;
  /** The page they asked from, a path inside the app. */
  public readonly returnPath: string | undefined;
  public readonly status: AccessRequestStatus;
  public readonly createdDate: AHDate;
  public readonly decidedByName: string | undefined;
  public readonly decidedDate: AHDate | undefined;
  public readonly decisionNote: string | undefined;
  /** Whether the reader may approve or deny it: pending, not theirs, and access they hold. */
  public readonly callerMayDecide: boolean;

  public get isPending(): boolean {
    return this.status === AccessRequestStatus.Pending;
  }

  private constructor(json: AccessRequestJSON) {
    this.id = new Guid(json.id);
    this.requesterUserId = new Guid(json.requesterUserId);
    this.requesterName = json.requesterName;
    this.access = readRequestableAccess(json.resource);
    this.accessDescription = json.accessDescription;
    this.context = json.context;
    this.returnPath = json.returnPath ?? undefined;
    this.status = json.status;
    this.createdDate = new AHDate(json.createdDate);
    this.decidedByName = json.decidedByName ?? undefined;
    this.decidedDate = json.decidedDate ? new AHDate(json.decidedDate) : undefined;
    this.decisionNote = json.decisionNote ?? undefined;
    this.callerMayDecide = json.callerMayDecide;
  }

  /** Whether this request asks for exactly this access. */
  public isFor(access: RequestableAccess): boolean {
    return isSameAccess(this.access, access);
  }
}

/** The pending request among these for exactly this access, if there is one. */
export function pendingRequestFor(
  requests: readonly AccessRequest[],
  access: RequestableAccess,
): AccessRequest | undefined {
  return requests.find((request) => request.isPending && request.isFor(access));
}
